import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Upload, Trash2, Plus, Edit, Star, Users, Clock, Image as ImageIcon, Building2, Calendar, ChevronDown, ChevronRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { type EventItem, resolveEventImageUrl, toDateInputValue, formatEventDateRange } from "@/lib/events";
import { RichTextEditor } from "@/components/admin/RichTextEditor";

interface AgendaFormRow {
  day: string;
  date: string;
  start_time: string;
  end_time: string;
  title: string;
  description: string;
}

interface GalleryFormRow {
  image_url: string;
  caption: string;
  is_featured: boolean;
}

const EMPTY_AGENDA_ROW: AgendaFormRow = {
  day: "Day 1",
  date: "",
  start_time: "",
  end_time: "",
  title: "",
  description: "",
};

function parseTimeRange(timeRange: string): { start: string; end: string } {
  const parts = timeRange.split(/\s[-–]\s/);
  return { start: parts[0]?.trim() || "", end: parts[1]?.trim() || "" };
}

function agendaRowsToPayload(rows: AgendaFormRow[]) {
  return rows
    .filter((row) => row.title.trim() && row.start_time.trim() && row.end_time.trim())
    .map((row, index) => ({
      day: row.day.trim() || "Day 1",
      date: row.date.trim() || undefined,
      time_range: `${row.start_time.trim()} – ${row.end_time.trim()}`,
      title: row.title.trim(),
      description: row.description.trim() || undefined,
      sort_order: index,
    }));
}

interface SpeakerOption {
  id: number;
  name: string;
  role: string;
  about: string | null;
  image_url: string | null;
}

interface SponsorOption {
  id: number;
  name: string;
  description: string | null;
  image_url: string | null;
}

function resolveSpeakerImageUrl(imageUrl: string | null): string | null {
  if (!imageUrl) return null;
  if (imageUrl.startsWith("http") || imageUrl.startsWith("/assets/")) return imageUrl;
  const base = import.meta.env.VITE_API_URL || "http://localhost:3001";
  return `${base}${imageUrl}`;
}

function resolveSponsorImageUrl(imageUrl: string | null): string | null {
  if (!imageUrl) return null;
  if (imageUrl.startsWith("http")) return imageUrl;
  const base = import.meta.env.VITE_API_URL || "http://localhost:3001";
  return `${base}${imageUrl}`;
}

interface EventFormData {
  title: string;
  subtitle: string;
  description: string;
  /** Start date of the event range (YYYY-MM-DD) */
  event_date_from: string;
  /** End date of the event range (YYYY-MM-DD) */
  event_date_to: string;
  location: string;
  category: string;
  is_featured: boolean;
  register_url: string;
  image_url: string | null;
  logo_url: string | null;
  venue_description: string;
  map_embed_url: string;
  hotel_name: string;
  phone: string;
  address: string;
  directions_link: string;
  directions: string;
  venue_image_url: string | null;
}

const EMPTY_FORM: EventFormData = {
  title: "",
  subtitle: "",
  description: "",
  event_date_from: "",
  event_date_to: "",
  location: "",
  category: "",
  is_featured: false,
  register_url: "",
  image_url: null,
  logo_url: null,
  venue_description: "",
  map_embed_url: "",
  hotel_name: "",
  phone: "",
  address: "",
  directions_link: "",
  directions: "",
  venue_image_url: null,
};

function eventToForm(event: EventItem): EventFormData {
  return {
    title: event.title || "",
    subtitle: event.subtitle || "",
    description: event.description || "",
    event_date_from: toDateInputValue(event.event_date_from ?? event.event_date),
    event_date_to: toDateInputValue(event.event_date_to ?? event.event_date_from ?? event.event_date),
    location: event.location || "",
    category: event.category || "",
    is_featured: Boolean(event.is_featured),
    register_url: event.register_url || "",
    image_url: event.image_url || null,
    logo_url: event.logo_url || null,
    venue_description: event.venue_description || "",
    map_embed_url: event.map_embed_url || "",
    hotel_name: event.hotel_name || "",
    phone: event.phone || "",
    address: event.address || "",
    directions_link: event.directions_link || "",
    directions: event.directions || "",
    venue_image_url: event.venue_image_url || null,
  };
}

function buildPayload(
  form: EventFormData,
  speakerIds: number[],
  sponsorIds: number[],
  agendaItems: AgendaFormRow[],
  galleryItems: GalleryFormRow[],
) {
  return {
    title: form.title.trim(),
    subtitle: form.subtitle.trim() || undefined,
    description: form.description.trim() || undefined,
    event_date_from: form.event_date_from.trim() || null,
    event_date_to: form.event_date_to.trim() || null,
    // Keep legacy field in sync using the start date
    event_date: form.event_date_from.trim() || undefined,
    location: form.location.trim() || undefined,
    category: form.category.trim() || null,
    is_featured: form.is_featured,
    register_url: form.register_url.trim() || "",
    image_url: form.image_url,
    logo_url: form.logo_url,
    venue_description: form.venue_description.trim() || null,
    map_embed_url: form.map_embed_url.trim() || null,
    hotel_name: form.hotel_name.trim() || null,
    phone: form.phone.trim() || null,
    address: form.address.trim() || null,
    directions_link: form.directions_link.trim() || null,
    directions: form.directions.trim() || null,
    venue_image_url: form.venue_image_url,
    speaker_ids: speakerIds,
    sponsor_ids: sponsorIds,
    agenda_items: agendaRowsToPayload(agendaItems),
    gallery_items: galleryItems.map((item, index) => ({
      image_url: item.image_url,
      caption: item.caption.trim() || null,
      is_featured: item.is_featured,
      sort_order: index,
    })),
  };
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <h3 className="font-serif text-base text-primary">{title}</h3>
      {children}
    </div>
  );
}

export function EventEditor() {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [allSpeakers, setAllSpeakers] = useState<SpeakerOption[]>([]);
  const [allSponsors, setAllSponsors] = useState<SponsorOption[]>([]);
  const [selectedSpeakerIds, setSelectedSpeakerIds] = useState<number[]>([]);
  const [selectedSponsorIds, setSelectedSponsorIds] = useState<number[]>([]);
  const [agendaItems, setAgendaItems] = useState<AgendaFormRow[]>([]);
  const [galleryItems, setGalleryItems] = useState<GalleryFormRow[]>([]);
  const [openDays, setOpenDays] = useState<Set<string>>(new Set(["Day 1"]));
  const [form, setForm] = useState<EventFormData>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadingField, setUploadingField] = useState<"image_url" | "logo_url" | "venue_image_url" | "gallery" | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const heroImageRef = useRef<HTMLInputElement>(null);
  const logoImageRef = useRef<HTMLInputElement>(null);
  const venueImageRef = useRef<HTMLInputElement>(null);
  const galleryImageRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadEvents();
    loadSpeakers();
    loadSponsors();
  }, []);

  const loadSpeakers = async () => {
    try {
      const response = await api.get<SpeakerOption[]>("/api/speakers");
      if (response.success && response.data) {
        setAllSpeakers(response.data);
      }
    } catch {
      console.error("Failed to load speakers");
    }
  };

  const loadSponsors = async () => {
    try {
      const response = await api.get<SponsorOption[]>("/api/sponsors");
      if (response.success && response.data) {
        setAllSponsors(response.data);
      }
    } catch {
      console.error("Failed to load sponsors");
    }
  };

  const loadEventSpeakers = async (eventId: number) => {
    try {
      const response = await api.get<SpeakerOption[]>(`/api/events/${eventId}/speakers`);
      if (response.success && response.data) {
        setSelectedSpeakerIds(response.data.map((s) => s.id));
      } else {
        setSelectedSpeakerIds([]);
      }
    } catch {
      setSelectedSpeakerIds([]);
    }
  };

  const loadEventSponsors = async (eventId: number) => {
    try {
      const response = await api.get<SponsorOption[]>(`/api/events/${eventId}/sponsors`);
      if (response.success && response.data) {
        setSelectedSponsorIds(response.data.map((s) => s.id));
      } else {
        setSelectedSponsorIds([]);
      }
    } catch {
      setSelectedSponsorIds([]);
    }
  };

  const loadEventAgenda = async (eventId: number) => {
    try {
      const response = await api.get<Array<{ day?: string; date?: string; time_range: string; title: string; description?: string }>>(
        `/api/events/${eventId}/agenda`,
      );
      if (response.success && response.data) {
        setAgendaItems(
          response.data.map((item) => {
            const { start, end } = parseTimeRange(item.time_range);
            return {
              day: (item as any).day || "Day 1",
              date: (item as any).date || "",
              start_time: start,
              end_time: end,
              title: item.title || "",
              description: item.description || "",
            };
          }),
        );
      } else {
        setAgendaItems([]);
      }
    } catch {
      setAgendaItems([]);
    }
  };

  const loadEventGallery = async (eventId: number) => {
    try {
      const response = await api.get<Array<{ image_url: string; caption: string | null; is_featured: boolean }>>(
        `/api/events/${eventId}/gallery`,
      );
      if (response.success && response.data) {
        setGalleryItems(
          response.data.map((item) => ({
            image_url: item.image_url,
            caption: item.caption || "",
            is_featured: Boolean(item.is_featured),
          })),
        );
      } else {
        setGalleryItems([]);
      }
    } catch {
      setGalleryItems([]);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await api.get<EventItem[]>("/api/events");
      if (response.success && response.data) {
        setEvents(response.data);
      }
    } catch {
      alert("Failed to load events");
    }
  };

  const openCreateDialog = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSelectedSpeakerIds([]);
    setSelectedSponsorIds([]);
    setAgendaItems([]);
    setGalleryItems([]);
    setIsDialogOpen(true);
  };

  const openEditDialog = async (event: EventItem) => {
    setEditingId(event.id);
    setForm(eventToForm(event));
    setIsDialogOpen(true);
    await Promise.all([
      loadEventSpeakers(event.id),
      loadEventSponsors(event.id),
      loadEventAgenda(event.id),
      loadEventGallery(event.id),
    ]);
  };

  const addAgendaRow = (day?: string) => {
    const lastDay = day || agendaItems[agendaItems.length - 1]?.day || "Day 1";
    setAgendaItems((prev) => [...prev, { ...EMPTY_AGENDA_ROW, day: lastDay }]);
  };

  const toggleDay = (day: string) => {
    setOpenDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  // Group agenda rows by day for the accordion display
  const agendaByDay: [string, number[]][] = (() => {
    const map = new Map<string, number[]>();
    agendaItems.forEach((row, idx) => {
      const d = row.day || "Day 1";
      if (!map.has(d)) map.set(d, []);
      map.get(d)!.push(idx);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  })();

  // When loading a saved event, open all its days
  useEffect(() => {
    const days = new Set(agendaItems.map((r) => r.day || "Day 1"));
    if (days.size > 0) setOpenDays(days);
  }, [agendaItems.length]);

  const updateAgendaRow = (index: number, field: keyof AgendaFormRow, value: string) => {
    setAgendaItems((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const removeAgendaRow = (index: number) => {
    setAgendaItems((prev) => prev.filter((_, i) => i !== index));
  };

  const removeGalleryItem = (index: number) => {
    setGalleryItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateGalleryItem = (index: number, field: keyof GalleryFormRow, value: string | boolean) => {
    setGalleryItems((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const toggleSpeaker = (speakerId: number, checked: boolean) => {
    setSelectedSpeakerIds((prev) =>
      checked ? [...prev, speakerId] : prev.filter((id) => id !== speakerId),
    );
  };

  const toggleSponsor = (sponsorId: number, checked: boolean) => {
    setSelectedSponsorIds((prev) =>
      checked ? [...prev, sponsorId] : prev.filter((id) => id !== sponsorId),
    );
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      alert("Event title is required");
      return;
    }
    if (!form.event_date_from.trim()) {
      alert("Event start date is required");
      return;
    }
    if (!form.location.trim()) {
      alert("Location is required");
      return;
    }

    setLoading(true);
    try {
      const payload = buildPayload(form, selectedSpeakerIds, selectedSponsorIds, agendaItems, galleryItems);

      if (editingId) {
        const response = await api.put<EventItem>(`/api/events/${editingId}`, payload);
        if (response.success) {
          alert("Event updated successfully");
          setIsDialogOpen(false);
          setEditingId(null);
          setForm(EMPTY_FORM);
          setSelectedSpeakerIds([]);
          setSelectedSponsorIds([]);
          setAgendaItems([]);
          setGalleryItems([]);
          await loadEvents();
        }
      } else {
        const response = await api.post<EventItem>("/api/events", payload);
        if (response.success) {
          alert("Event created successfully");
          setIsDialogOpen(false);
          setForm(EMPTY_FORM);
          setSelectedSpeakerIds([]);
          setSelectedSponsorIds([]);
          setAgendaItems([]);
          setGalleryItems([]);
          await loadEvents();
        }
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to save event";
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this event? This cannot be undone.")) return;

    setLoading(true);
    try {
      const response = await api.delete(`/api/events/${id}`);
      if (response.success) {
        setEvents((prev) => prev.filter((e) => e.id !== id));
        alert("Event deleted successfully");
      }
    } catch {
      alert("Failed to delete event");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof EventFormData, value: string | boolean | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploadingField("gallery");
    try {
      const token = localStorage.getItem("adminToken");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";

      const uploaded: GalleryFormRow[] = [];
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("image", file);

        const response = await fetch(`${baseUrl}/api/upload`, {
          method: "POST",
          headers,
          body: formData,
        });

        const result = await response.json();
        if (result.success && result.data?.imageUrl) {
          uploaded.push({ image_url: result.data.imageUrl, caption: "", is_featured: false });
        }
      }

      if (uploaded.length > 0) {
        setGalleryItems((prev) => [...prev, ...uploaded]);
      } else {
        alert("Failed to upload images");
      }
    } catch {
      alert("Failed to upload images");
    } finally {
      setUploadingField(null);
      e.target.value = "";
    }
  };

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "image_url" | "logo_url" | "venue_image_url",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingField(field);
    try {
      const formData = new FormData();
      formData.append("image", file);

      const token = localStorage.getItem("adminToken");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:3001"}/api/upload`, {
        method: "POST",
        headers,
        body: formData,
      });

      const result = await response.json();
      if (result.success && result.data?.imageUrl) {
        setForm((prev) => ({ ...prev, [field]: result.data.imageUrl }));
      } else {
        alert("Failed to upload image");
      }
    } catch {
      alert("Failed to upload image");
    } finally {
      setUploadingField(null);
      e.target.value = "";
    }
  };

  const renderImageUpload = (
    field: "image_url" | "logo_url" | "venue_image_url",
    label: string,
    inputRef: React.RefObject<HTMLInputElement | null>,
  ) => {
    const url = form[field];
    const uploading = uploadingField === field;

    return (
      <div>
        <Label>{label}</Label>
        <div className="flex gap-2 mt-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, field)}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={uploadingField !== null}
            className="flex-1"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? "Uploading..." : "Upload Image"}
          </Button>
          {url && (
            <Button type="button" variant="outline" onClick={() => handleChange(field, null)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        {url && (
          <div className="mt-3 rounded-lg overflow-hidden border bg-muted/30 p-3">
            <img
              src={resolveEventImageUrl(url)}
              alt="Preview"
              className={
                field === "logo_url"
                  ? "mx-auto h-20 w-auto max-w-full object-contain"
                  : "w-full h-40 object-cover"
              }
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-primary mb-2">Events</h1>
          <p className="text-muted-foreground">
            Add and manage multiple events with full details — newest uploads appear first on the homepage slider.
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Event" : "Add New Event"}</DialogTitle>
              <DialogDescription>
                Complete all event details including speakers, sponsors, venue, agenda, gallery, and registration.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto space-y-6 pr-1">
              <FormSection title="Basic Information">
                <div>
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    placeholder="e.g., The Third Arab Capital Markets Conference"
                  />
                </div>

                <div>
                  <Label htmlFor="subtitle">Subtitle / Theme</Label>
                  <Input
                    id="subtitle"
                    value={form.subtitle}
                    onChange={(e) => handleChange("subtitle", e.target.value)}
                    placeholder="e.g., Innovation, AI & Sustainability"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <RichTextEditor
                    content={form.description}
                    onChange={(val) => handleChange("description", val)}
                    placeholder="Full event description"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="event_date_from">Event Start Date *</Label>
                    <Input
                      id="event_date_from"
                      type="date"
                      value={form.event_date_from}
                      onChange={(e) => handleChange("event_date_from", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="event_date_to">Event End Date</Label>
                    <Input
                      id="event_date_to"
                      type="date"
                      min={form.event_date_from || undefined}
                      value={form.event_date_to}
                      onChange={(e) => handleChange("event_date_to", e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      id="location"
                      value={form.location}
                      onChange={(e) => handleChange("location", e.target.value)}
                      placeholder="e.g., Doha, Qatar"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Input
                      id="category"
                      value={form.category}
                      onChange={(e) => handleChange("category", e.target.value)}
                      placeholder="e.g., Conference, Forum, Workshop"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Switch
                    id="is_featured"
                    checked={form.is_featured}
                    onCheckedChange={(v) => handleChange("is_featured", v)}
                  />
                  <Label htmlFor="is_featured">Featured on homepage</Label>
                </div>
              </FormSection>

              <Separator />

              <FormSection title="Speakers">
                <p className="text-sm text-muted-foreground">
                  Select speakers for this event. Manage speaker profiles under Admin → Speakers.
                </p>
                {allSpeakers.length === 0 ? (
                  <Card className="p-4 border-dashed text-sm text-muted-foreground">
                    No speakers available. Add speakers first from the Speakers admin page.
                  </Card>
                ) : (
                  <div className="max-h-64 overflow-y-auto rounded-lg border divide-y">
                    {allSpeakers.map((speaker) => {
                      const checked = selectedSpeakerIds.includes(speaker.id);
                      const imageUrl = resolveSpeakerImageUrl(speaker.image_url);
                      return (
                        <label
                          key={speaker.id}
                          className="flex items-start gap-3 p-3 hover:bg-secondary/50 cursor-pointer"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) => toggleSpeaker(speaker.id, value === true)}
                            className="mt-1"
                          />
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt=""
                              className="w-10 h-10 rounded-full object-cover shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <Users className="w-4 h-4 text-primary" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm text-primary leading-snug">{speaker.name}</div>
                            <div className="text-xs text-muted-foreground">{speaker.role}</div>
                            {speaker.about && (
                              <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{speaker.about}</div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {selectedSpeakerIds.length} speaker{selectedSpeakerIds.length !== 1 ? "s" : ""} selected
                </p>
              </FormSection>

              <Separator />

              <FormSection title="Sponsors">
                <p className="text-sm text-muted-foreground">
                  Select sponsors for this event. Manage sponsor profiles under Admin → Sponsors.
                </p>
                {allSponsors.length === 0 ? (
                  <Card className="p-4 border-dashed text-sm text-muted-foreground">
                    No sponsors available. Add sponsors first from the Sponsors admin page.
                  </Card>
                ) : (
                  <div className="max-h-64 overflow-y-auto rounded-lg border divide-y">
                    {allSponsors.map((sponsor) => {
                      const checked = selectedSponsorIds.includes(sponsor.id);
                      const imageUrl = resolveSponsorImageUrl(sponsor.image_url);
                      return (
                        <label
                          key={sponsor.id}
                          className="flex items-start gap-3 p-3 hover:bg-secondary/50 cursor-pointer"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) => toggleSponsor(sponsor.id, value === true)}
                            className="mt-1"
                          />
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt=""
                              className="w-10 h-10 rounded object-contain bg-white shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center shrink-0">
                              <Building2 className="w-4 h-4 text-primary" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm text-primary leading-snug">{sponsor.name}</div>
                            {sponsor.description && (
                              <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                                {sponsor.description.replace(/<[^>]*>/g, " ").trim()}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {selectedSponsorIds.length} sponsor{selectedSponsorIds.length !== 1 ? "s" : ""} selected
                </p>
              </FormSection>

              <Separator />

              <FormSection title="Agenda">
                <p className="text-sm text-muted-foreground">
                  Organise sessions by day. Each day can be expanded or collapsed. Add a new day with the button below.
                </p>

                {agendaItems.length === 0 ? (
                  <Card className="p-4 border-dashed text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4 shrink-0" />
                    No agenda items yet. Add the first session below.
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {agendaByDay.map(([day, indices]) => (
                      <Collapsible
                        key={day}
                        open={openDays.has(day)}
                        onOpenChange={() => toggleDay(day)}
                      >
                        <Card className="overflow-hidden">
                          {/* Day header */}
                          <CollapsibleTrigger asChild>
                            <button className="w-full flex items-center justify-between px-4 py-3 bg-accent/10 hover:bg-accent/20 transition-colors text-left">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-accent" />
                                <span className="font-semibold text-primary">{day}</span>
                                {agendaItems[indices[0]]?.date && (
                                  <Badge variant="outline" className="text-xs font-normal">
                                    {agendaItems[indices[0]].date}
                                  </Badge>
                                )}
                                <Badge variant="secondary" className="text-xs">
                                  {indices.length} {indices.length === 1 ? "item" : "items"}
                                </Badge>
                              </div>
                              {openDays.has(day) ? (
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              )}
                            </button>
                          </CollapsibleTrigger>

                          <CollapsibleContent>
                            <div className="p-4 space-y-4">
                              {/* Date input for the day — shared across all items in this day */}
                              <div className="flex items-center gap-3">
                                <Label className="shrink-0 text-xs text-muted-foreground w-16">Day date</Label>
                                <Input
                                  type="date"
                                  className="w-44 h-8 text-xs"
                                  value={agendaItems[indices[0]]?.date || ""}
                                  onChange={(e) => {
                                    // Apply the chosen date to ALL items in this day
                                    setAgendaItems((prev) =>
                                      prev.map((row, i) =>
                                        indices.includes(i) ? { ...row, date: e.target.value } : row
                                      )
                                    );
                                  }}
                                />
                              </div>

                              {indices.map((itemIndex) => {
                                const row = agendaItems[itemIndex];
                                return (
                                  <Card key={itemIndex} className="p-4 space-y-3 border-l-4 border-l-accent/30">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-sm font-medium text-primary">
                                          Session {indices.indexOf(itemIndex) + 1}
                                        </span>
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeAgendaRow(itemIndex)}
                                        className="text-destructive hover:text-destructive h-8 w-8"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <Label className="text-xs">Start Time</Label>
                                        <Input
                                          type="time"
                                          value={row.start_time}
                                          onChange={(e) => updateAgendaRow(itemIndex, "start_time", e.target.value)}
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs">End Time</Label>
                                        <Input
                                          type="time"
                                          value={row.end_time}
                                          onChange={(e) => updateAgendaRow(itemIndex, "end_time", e.target.value)}
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <Label className="text-xs">Title</Label>
                                      <Input
                                        value={row.title}
                                        onChange={(e) => updateAgendaRow(itemIndex, "title", e.target.value)}
                                        placeholder="Session title"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Description</Label>
                                      <RichTextEditor
                                        content={row.description}
                                        onChange={(val) => updateAgendaRow(itemIndex, "description", val)}
                                        placeholder="Optional session details"
                                        minHeight="80px"
                                      />
                                    </div>
                                  </Card>
                                );
                              })}

                              {/* Add session to this specific day */}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => addAgendaRow(day)}
                                className="w-full border border-dashed"
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Add session to {day}
                              </Button>
                            </div>
                          </CollapsibleContent>
                        </Card>
                      </Collapsible>
                    ))}
                  </div>
                )}

                {/* Add new day buttons */}
                <div className="flex flex-wrap gap-2">
                  {["Day 1", "Day 2", "Day 3"].map((d) => {
                    const exists = agendaByDay.some(([day]) => day === d);
                    return (
                      <Button
                        key={d}
                        type="button"
                        variant={exists ? "secondary" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (!exists) {
                            setAgendaItems((prev) => [...prev, { ...EMPTY_AGENDA_ROW, day: d }]);
                            setOpenDays((prev) => new Set([...prev, d]));
                          } else {
                            // Just scroll/open the existing day
                            setOpenDays((prev) => new Set([...prev, d]));
                          }
                        }}
                      >
                        <Calendar className="w-3.5 h-3.5 mr-1.5" />
                        {exists ? `Open ${d}` : `Add ${d}`}
                      </Button>
                    );
                  })}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const nextNum = (agendaByDay.length || 0) + 1;
                      const newDay = `Day ${nextNum}`;
                      setAgendaItems((prev) => [...prev, { ...EMPTY_AGENDA_ROW, day: newDay }]);
                      setOpenDays((prev) => new Set([...prev, newDay]));
                    }}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    Add custom day
                  </Button>
                </div>
              </FormSection>

              <Separator />

              <FormSection title="Registration">
                <div>
                  <Label htmlFor="register_url">Registration URL</Label>
                  <Input
                    id="register_url"
                    value={form.register_url}
                    onChange={(e) => handleChange("register_url", e.target.value)}
                    placeholder="https://forms.office.com/..."
                  />
                </div>
              </FormSection>

              <Separator />

              <FormSection title="Branding & Hero">
                {renderImageUpload("logo_url", "Event Logo (shown in site header)", logoImageRef)}
                {renderImageUpload("image_url", "Event Hero Image (homepage slider)", heroImageRef)}
              </FormSection>

              <Separator />

              <FormSection title="Venue Details">
                <div>
                  <Label htmlFor="venue_description">Venue Description</Label>
                  <RichTextEditor
                    content={form.venue_description}
                    onChange={(val) => handleChange("venue_description", val)}
                    placeholder="Describe the venue and facilities"
                  />
                </div>

                <div>
                  <Label htmlFor="hotel_name">Hotel / Venue Name</Label>
                  <Input
                    id="hotel_name"
                    value={form.hotel_name}
                    onChange={(e) => handleChange("hotel_name", e.target.value)}
                    placeholder="e.g., Sheraton Grand Doha"
                  />
                </div>

                <div>
                  <Label htmlFor="address">Address</Label>
                  <RichTextEditor
                    content={form.address}
                    onChange={(val) => handleChange("address", val)}
                    placeholder="Full street address"
                    minHeight="80px"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Contact Phone</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="e.g., +974 1234 5678"
                  />
                </div>

                <div>
                  <Label htmlFor="map_embed_url">Map Embed URL</Label>
                  <Input
                    id="map_embed_url"
                    value={form.map_embed_url}
                    onChange={(e) => handleChange("map_embed_url", e.target.value)}
                    placeholder="Google Maps or OpenStreetMap embed URL"
                  />
                </div>

                <div>
                  <Label htmlFor="directions_link">Directions Link</Label>
                  <Input
                    id="directions_link"
                    value={form.directions_link}
                    onChange={(e) => handleChange("directions_link", e.target.value)}
                    placeholder="https://maps.google.com/..."
                  />
                </div>

                <div>
                  <Label htmlFor="directions">Directions Text</Label>
                  <RichTextEditor
                    content={form.directions}
                    onChange={(val) => handleChange("directions", val)}
                    placeholder="Travel and arrival instructions for delegates"
                  />
                </div>

                {renderImageUpload("venue_image_url", "Venue Image", venueImageRef)}
              </FormSection>

              <Separator />

              <FormSection title="Gallery">
                <p className="text-sm text-muted-foreground">
                  Upload photos for this event. Mark one as featured to highlight it on the gallery page.
                </p>
                <div>
                  <input
                    ref={galleryImageRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => galleryImageRef.current?.click()}
                    disabled={uploadingField !== null}
                    className="w-full"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingField === "gallery" ? "Uploading..." : "Upload Gallery Images"}
                  </Button>
                </div>
                {galleryItems.length === 0 ? (
                  <Card className="p-4 border-dashed text-sm text-muted-foreground flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 shrink-0" />
                    No gallery images yet.
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {galleryItems.map((item, index) => (
                      <Card key={index} className="overflow-hidden">
                        <div className="relative">
                          <img
                            src={resolveEventImageUrl(item.image_url)}
                            alt=""
                            className="w-full h-32 object-cover"
                          />
                          {item.is_featured && (
                            <Badge className="absolute top-2 left-2 bg-accent text-accent-foreground text-[10px]">
                              <Star className="w-3 h-3 mr-1 fill-current" />
                              Featured
                            </Badge>
                          )}
                        </div>
                        <div className="p-3 space-y-2">
                          <Input
                            value={item.caption}
                            onChange={(e) => updateGalleryItem(index, "caption", e.target.value)}
                            placeholder="Caption (optional)"
                            className="text-sm"
                          />
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <Switch
                                checked={item.is_featured}
                                onCheckedChange={(v) => updateGalleryItem(index, "is_featured", v)}
                              />
                              Featured
                            </label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeGalleryItem(index)}
                              className="text-destructive hover:text-destructive h-8 w-8"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {galleryItems.length} image{galleryItems.length !== 1 ? "s" : ""} in gallery
                </p>
              </FormSection>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                {loading ? "Saving..." : editingId ? "Update Event" : "Create Event"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        {events.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <p className="mb-4">No events yet.</p>
            <Button onClick={openCreateDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Event
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Image</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>
                    <img
                      src={resolveEventImageUrl(event.image_url)}
                      alt=""
                      className="w-12 h-12 rounded object-cover"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-primary">{event.title}</div>
                    {event.subtitle && (
                      <div className="text-xs text-muted-foreground line-clamp-1 italic">{event.subtitle}</div>
                    )}
                    {Boolean(event.is_featured) && (
                      <Badge className="mt-1 bg-accent text-accent-foreground text-[10px]">
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        Featured
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{formatEventDateRange(event) || "—"}</TableCell>
                  <TableCell className="text-sm">{event.location || "—"}</TableCell>
                  <TableCell className="text-sm">{event.category || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(event)} title="Edit">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(event.id)}
                        className="text-destructive hover:text-destructive"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <p className="text-sm text-muted-foreground">
        {events.length} event{events.length !== 1 ? "s" : ""} total. Fields marked * are required.
      </p>
    </div>
  );
}
